/**
 * Geo location modal: offer language links when the visitor country differs
 * from the current page (live-site location-modal port).
 */

/* global strl_vars */

const DISMISS_KEY = 'location_modal_dismissed';

const getCookie = (name) => {
	const row = document.cookie
		.split(';')
		.map((c) => c.trim())
		.find((c) => c.startsWith(name + '='));
	return row ? decodeURIComponent(row.split('=').slice(1).join('=')) : null;
};

const setCookie = (name, value, days = 365) => {
	const date = new Date();
	date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
	document.cookie = `${name}=${value}; expires=${date.toUTCString()}; path=/; secure; samesite=Lax`;
};

const dismissModal = () => {
	try {
		sessionStorage.setItem(DISMISS_KEY, 'true');
	} catch (_) {
		// noop
	}
};

const isDismissed = () => {
	try {
		return sessionStorage.getItem(DISMISS_KEY) === 'true';
	} catch (_) {
		return false;
	}
};

const findCountryRow = (modal, iso) => {
	const code = (iso || '').toString().trim().toLowerCase();
	if (!code) {
		return null;
	}
	return (
		modal.querySelector(`[data-location-country][data-country-iso="${code}"]`) ||
		modal.querySelector(`[data-location-country][data-country="${code}"]`)
	);
};

const hideAllVariants = (modal) => {
	modal.querySelectorAll('[data-location-title], [data-location-desc]').forEach((el) => {
		el.setAttribute('hidden', '');
	});
};

const openModal = (modal) => {
	modal.removeAttribute('hidden');
	modal.setAttribute('aria-hidden', 'false');
	document.documentElement.classList.add('overflow-hidden');
	document.body.classList.add('overflow-hidden');
};

const closeModal = (modal) => {
	modal.setAttribute('hidden', '');
	modal.setAttribute('aria-hidden', 'true');
	document.documentElement.classList.remove('overflow-hidden');
	document.body.classList.remove('overflow-hidden');
};

const showLanguageSelect = (modal, countryEl) => {
	hideAllVariants(modal);
	modal.querySelector('[data-location-title="select"]')?.removeAttribute('hidden');
	modal.querySelector('[data-location-desc="language"]')?.removeAttribute('hidden');
	countryEl?.removeAttribute('hidden');
	if (modal.id) {
		modal.setAttribute('aria-labelledby', `${modal.id}-title-select`);
	}
	openModal(modal);
};

const showWelcome = (modal, countryEl) => {
	hideAllVariants(modal);
	modal.querySelector('[data-location-title="welcome"]')?.removeAttribute('hidden');
	if (modal.id) {
		modal.setAttribute('aria-labelledby', `${modal.id}-title-welcome`);
	}
	const desc = modal.querySelector('[data-location-desc="country-and-language"]');
	const label = modal.querySelector('[data-location-country-label]');
	const name = countryEl?.querySelector('[data-location-country-name]')?.textContent?.trim() || '';
	if (label && name) {
		label.textContent = name;
	}
	desc?.removeAttribute('hidden');
	countryEl?.removeAttribute('hidden');
	openModal(modal);
};

const fetchCountryCode = async () => {
	const ajaxUrl = (typeof strl_vars !== 'undefined' && strl_vars.ajax_url) || '/wp-admin/admin-ajax.php';
	const nonce = (typeof strl_vars !== 'undefined' && strl_vars.location_modal_nonce) || '';
	const form = new FormData();
	form.append('action', 'get_location_country');
	form.append('nonce', nonce);

	const resp = await fetch(ajaxUrl, {
		method: 'POST',
		body: form,
		credentials: 'same-origin',
		cache: 'no-store',
	});
	const json = await resp.json();
	if (!json || !json.success || !json.data) {
		return '';
	}
	return (json.data.country || '').toString().trim().toLowerCase();
};

const bindUi = (modal) => {
	modal.addEventListener('click', (event) => {
		if (event.target.closest('[data-location-modal-close]')) {
			dismissModal();
			closeModal(modal);
			return;
		}
		if (event.target === modal || !event.target.closest('[data-location-modal-panel]')) {
			dismissModal();
			closeModal(modal);
		}
	});

	document.addEventListener('keydown', (event) => {
		if (event.key !== 'Escape' || modal.hasAttribute('hidden')) {
			return;
		}
		dismissModal();
		closeModal(modal);
	});

	modal.querySelectorAll('[data-location-modal-language]').forEach((link) => {
		link.addEventListener('click', (event) => {
			event.preventDefault();
			const country = link.getAttribute('data-country') || 'global';
			const language = link.getAttribute('data-language') || 'en';
			const flagUrl = link.getAttribute('data-flag-url') || '';

			const countryIso =
				link.closest('[data-location-country]')?.getAttribute('data-country-iso') || country;

			setCookie('era_country_pref', country);
			setCookie('wp-wpml_current_language', language);
			setCookie('eraneos_flag_url', flagUrl || 'global');
			setCookie(`strl_geo_loc_${countryIso}`, language);
			dismissModal();

			try {
				document.dispatchEvent(
					new CustomEvent('countryPreferenceChanged', {
						detail: { country, language, flagUrl: flagUrl || null },
					})
				);
			} catch (_) {
				// noop
			}

			const href = link.getAttribute('href');
			if (href) {
				window.setTimeout(() => {
					window.location.href = href;
				}, 100);
			}
		});
	});
};

const handleGeoLocation = async (modal) => {
	if (isDismissed()) {
		return;
	}

	const params = new URLSearchParams(window.location.search);
	if (params.has('country')) {
		dismissModal();
		return;
	}

	let userCountryCode = '';
	try {
		userCountryCode = await fetchCountryCode();
	} catch (_) {
		return;
	}

	if (!userCountryCode) {
		return;
	}

	const pageCountryCode = (modal.getAttribute('data-country') || '').toLowerCase();
	const countryEl = findCountryRow(modal, userCountryCode);
	const savedLanguagePref = getCookie(`strl_geo_loc_${userCountryCode}`) || getCookie('wp-wpml_current_language') || '';

	if (userCountryCode === pageCountryCode) {
		if (savedLanguagePref === pageCountryCode) {
			return;
		}

		if (!countryEl) {
			return;
		}

		const languageLink = savedLanguagePref
			? countryEl.querySelector(`[data-location-modal-language][data-language="${savedLanguagePref}"]`)
			: null;

		if (countryEl && languageLink?.hasAttribute('href')) {
			return;
		}

		showLanguageSelect(modal, countryEl);
		return;
	}

	if (!countryEl || userCountryCode === 'global') {
		return;
	}

	showWelcome(modal, countryEl);
};

document.addEventListener('DOMContentLoaded', () => {
	const modal = document.querySelector('[data-location-modal]');
	if (!modal) {
		return;
	}
	bindUi(modal);
	handleGeoLocation(modal);
});
